import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const colors = {
  mainBlue: "#061941",
  xanthous: "#F2AF29",
  isabelline: "#F4F3EE",
  carrieban: "#3C6E71",
  cafeNoir: "#583E23",
};

export default function SelectRole() {
  const [userData, setUserData] = useState(null);
  const [role, setRole] = useState('student');
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if userData is passed via state (Google users)
    if (location.state?.userData) {
      setUserData(location.state.userData);
    } else {
      // Fallback to URL params (legacy Google OAuth)
      const params = new URLSearchParams(location.search);
      const userDataParam = params.get('userData');
      if (userDataParam) {
        setUserData(JSON.parse(decodeURIComponent(userDataParam)));
      }
    }
  }, [location]);

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
    return emailRegex.test(email) && email.includes('.') && email.split('.').pop().length >= 2;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Validate email format
    if (!isValidEmail(userData.email)) {
      alert('❌ Please enter a valid email address.');
      setLoading(false);
      return;
    }
    
    // Validate professor email
    if (role === 'professor' && !userData.email.toLowerCase().endsWith('.edu')) {
      alert('❌ Professors must use a .edu email address.');
      setLoading(false);
      return;
    }
    
    try {
      if (userData.isGoogleUser) {
        // For Google users, update their role via Firebase endpoint
        const token = localStorage.getItem('token');
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/firebase/update-role`, {
          role: role
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Update localStorage with new role
        const currentUser = JSON.parse(localStorage.getItem('user'));
        currentUser.role = role;
        localStorage.setItem('user', JSON.stringify(currentUser));
        
        alert('✅ Role updated successfully!');
        
        // Redirect to appropriate dashboard
        if (role === 'student') {
          navigate('/student-welcome');
        } else if (role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/professor-welcome');
        }
      } else {
        // Legacy Google OAuth flow
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google/complete`, {
          ...userData,
          role
        });
        
        alert('✅ Registration completed successfully!');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error(error);
      if (error.response?.data?.message) {
        alert(`❌ ${error.response.data.message}`);
      } else {
        alert('❌ Registration failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!userData) {
    return <div>Loading...</div>;
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: `linear-gradient(120deg, ${colors.mainBlue} 70%, ${colors.xanthous} 92%, ${colors.xanthous} 100%)`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "Segoe UI, Arial, sans-serif"
    }}>
      <div style={{
        width: "100%",
        maxWidth: "500px",
        background: colors.isabelline,
        borderRadius: "16px",
        boxShadow: `0 4px 24px 0 ${colors.cafeNoir}22`,
        padding: "2.5rem 1.75rem",
        margin: "2rem",
      }}>
        <h2 style={{
          fontSize: "2.2rem",
          fontWeight: 800,
          textAlign: "center",
          color: colors.mainBlue,
          marginBottom: "1rem",
        }}>
          Welcome, {userData.name}!
        </h2>
        
        <p style={{
          textAlign: "center",
          color: colors.carrieban,
          marginBottom: "1rem",
          fontSize: "1.1rem"
        }}>
          Please select your role to complete your registration:
        </p>
        
        {userData.isGoogleUser && (
          <div style={{
            textAlign: "center",
            marginBottom: "1.5rem",
            padding: "1rem",
            background: `${colors.xanthous}15`,
            borderRadius: "12px",
            border: `2px solid ${colors.xanthous}40`
          }}>
            <p style={{
              margin: 0,
              fontSize: "1rem",
              color: colors.carrieban,
              fontWeight: 600
            }}>
              🎓 Choose your role to complete registration
            </p>
            {userData.email.toLowerCase().endsWith('.edu') && (
              <p style={{
                margin: "0.5rem 0 0 0",
                fontSize: "0.9rem",
                color: colors.carrieban,
                fontStyle: "italic"
              }}>
                ✅ Your .edu email allows you to register as either Student or Professor
              </p>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "2rem" }}>
            <div style={{ display: "flex", gap: "1.5rem", justifyContent: "center", flexWrap: "wrap" }}>
              <label style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.75rem",
                cursor: "pointer",
                padding: "1.5rem 2rem",
                border: `3px solid ${role === 'student' ? colors.xanthous : colors.carrieban}40`,
                borderRadius: "15px",
                backgroundColor: role === 'student' ? `${colors.xanthous}20` : 'transparent',
                transition: "all 0.3s",
                minWidth: "140px",
                textAlign: "center"
              }}>
                <input
                  type="radio"
                  value="student"
                  checked={role === 'student'}
                  onChange={(e) => setRole(e.target.value)}
                  style={{ transform: "scale(1.2)" }}
                />
                <div>
                  <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>🎓</div>
                  <span style={{ fontWeight: 700, color: colors.carrieban, fontSize: "1.1rem" }}>Student</span>
                  <div style={{ fontSize: "0.8rem", color: colors.carrieban, marginTop: "0.25rem", opacity: 0.8 }}>
                    Looking for research opportunities
                  </div>
                </div>
              </label>
              
              <label style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.75rem",
                cursor: userData.email.toLowerCase().endsWith('.edu') ? "pointer" : "not-allowed",
                padding: "1.5rem 2rem",
                border: `3px solid ${role === 'professor' ? colors.xanthous : 
                  userData.email.toLowerCase().endsWith('.edu') ? colors.carrieban + '40' : colors.carrieban + '20'}`,
                borderRadius: "15px",
                backgroundColor: role === 'professor' ? `${colors.xanthous}20` : 
                  userData.email.toLowerCase().endsWith('.edu') ? 'transparent' : `${colors.carrieban}10`,
                transition: "all 0.3s",
                minWidth: "140px",
                textAlign: "center",
                opacity: userData.email.toLowerCase().endsWith('.edu') ? 1 : 0.6
              }}>
                <input
                  type="radio"
                  value="professor"
                  checked={role === 'professor'}
                  onChange={(e) => setRole(e.target.value)}
                  disabled={!userData.email.toLowerCase().endsWith('.edu')}
                  style={{ transform: "scale(1.2)" }}
                />
                <div>
                  <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>👨‍🏫</div>
                  <span style={{ fontWeight: 700, color: colors.carrieban, fontSize: "1.1rem" }}>Professor</span>
                  <div style={{ fontSize: "0.8rem", color: colors.carrieban, marginTop: "0.25rem", opacity: 0.8 }}>
                    {userData.email.toLowerCase().endsWith('.edu') ? 
                      "Posting research positions" : 
                      "Requires .edu email"
                    }
                  </div>
                </div>
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              background: colors.xanthous,
              color: colors.mainBlue,
              padding: "1rem 0",
              borderRadius: "10px",
              fontWeight: 700,
              fontSize: "1.15rem",
              border: "none",
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? "Completing Registration..." : "Complete Registration"}
          </button>
        </form>
      </div>
    </div>
  );
}